export const AUTH_TOKEN_KEY = "token";
export const NOTIFICATION_COUNT_KEY = "notificationCount";
export const CART_KEY = "cart";
export const DISCOUNT_KEY = "discount";
export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";
export const LOCAL_WISHLIST_KEY = "localWishlist";

const hasLocalStorage = () =>
  typeof window !== "undefined" && window.localStorage;

const safeGetItem = (key) => {
  if (!hasLocalStorage()) return null;
  return window.localStorage.getItem(key);
};

const safeSetItem = (key, value) => {
  if (!hasLocalStorage()) return;
  window.localStorage.setItem(key, value);
};

const safeRemoveItem = (key) => {
  if (!hasLocalStorage()) return;
  window.localStorage.removeItem(key);
};

const hasSessionStorage = () =>
  typeof window !== "undefined" && window.sessionStorage;

export const setPostLoginRedirect = (path) => {
  if (!hasSessionStorage() || typeof path !== "string" || !path.startsWith("/"))
    return;
  window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
};

export const getPostLoginRedirect = () => {
  if (!hasSessionStorage()) return null;
  return window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
};

export const removePostLoginRedirect = () => {
  if (!hasSessionStorage()) return;
  window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
};

const safeJsonParse = (value) => {
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getAuthToken = () => {
  const token = safeGetItem(AUTH_TOKEN_KEY);
  if (!token) return null;

  if (!token.includes(".") && /^[A-Za-z0-9+/=]+$/.test(token)) {
    try {
      const binary = atob(token);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      const decoded = new TextDecoder().decode(bytes);
      if (decoded && decoded.length > 0) {
        safeSetItem(AUTH_TOKEN_KEY, decoded);
        return decoded;
      }
    } catch {}
  }

  return token;
};

export const setAuthToken = (token) => {
  if (typeof token !== "string" || !token || token.length < 10) {
    return;
  }
  safeSetItem(AUTH_TOKEN_KEY, token);
};

export const removeAuthToken = () => safeRemoveItem(AUTH_TOKEN_KEY);

export const getStoredJson = (key) => {
  const stored = safeGetItem(key);
  return stored ? safeJsonParse(stored) : null;
};

export const setStoredJson = (key, value) => {
  if (value === undefined || value === null) {
    safeRemoveItem(key);
    return;
  }
  safeSetItem(key, JSON.stringify(value));
};

export const getStoredNumber = (key) => safeNumber(safeGetItem(key));
export const setStoredNumber = (key, value) =>
  safeSetItem(key, String(safeNumber(value)));
export const removeStored = (key) => safeRemoveItem(key);

export const getLocalWishlist = () => {
  const wishlist = getStoredJson(LOCAL_WISHLIST_KEY);
  if (!Array.isArray(wishlist)) return [];
  return wishlist
    .map((item) => (item && typeof item === "object" ? item : { id: item }))
    .filter((item) => item.id != null);
};

export const getLocalWishlistIds = () =>
  getLocalWishlist().map((item) => item.id);

export const addToLocalWishlist = (product) => {
  if (!product || product.id == null) return getLocalWishlist();
  const wishlist = getLocalWishlist().filter(
    (item) => String(item.id) !== String(product.id),
  );
  wishlist.push(product);
  setStoredJson(LOCAL_WISHLIST_KEY, wishlist);
  return wishlist;
};

export const removeFromLocalWishlist = (productId) => {
  const wishlist = getLocalWishlist().filter(
    (item) => String(item.id) !== String(productId),
  );
  setStoredJson(LOCAL_WISHLIST_KEY, wishlist);
  return wishlist;
};

export const getNotificationCountStorage = () =>
  getStoredNumber(NOTIFICATION_COUNT_KEY);
export const setNotificationCountStorage = (count) =>
  setStoredNumber(NOTIFICATION_COUNT_KEY, count);
export const clearNotificationCountStorage = () =>
  removeStored(NOTIFICATION_COUNT_KEY);
